// Account tab: identity, notification enablement (with the iOS install caveat),
// and sign out.
import { useState } from "react";
import { api } from "../lib/api";
import { useSignOut } from "../lib/auth";
import { isIos, isStandalone, pushSupported, subscribeToPush } from "../lib/pwa";
import { Button, Card, ScreenHeader } from "./ui";

export function Account({ email, tier }: { email: string; tier?: string }) {
  const signOut = useSignOut();
  const [pushState, setPushState] = useState<"idle" | "working" | "on" | "denied" | "error">(
    typeof Notification !== "undefined" && Notification.permission === "granted" ? "on" : "idle",
  );

  // iOS only allows Web Push for a home-screen-installed PWA.
  const iosNeedsInstall = isIos() && !isStandalone();

  async function enablePush() {
    setPushState("working");
    try {
      const { publicKey } = await api.get<{ publicKey: string | null }>("/api/push/vapid");
      if (!publicKey) {
        setPushState("error");
        return;
      }
      const ok = await subscribeToPush(publicKey);
      setPushState(ok ? "on" : "denied");
    } catch {
      setPushState("error");
    }
  }

  return (
    <div className="pb-4">
      <ScreenHeader title="Account" />

      <div className="px-4 flex flex-col gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-navy-soft text-navy flex items-center justify-center">
              <i className="ph ph-user text-xl" />
            </div>
            <div className="min-w-0">
              <div className="text-ink font-medium truncate">{email}</div>
              {tier === "club" ? (
                <span className="inline-flex items-center gap-1 text-[12px] text-orange font-medium">
                  <i className="ph-fill ph-star" /> Club member
                </span>
              ) : (
                <span className="text-[12px] text-ink-subtle">Signed in</span>
              )}
            </div>
          </div>
        </Card>

        {/* Notifications */}
        <Card className="p-4">
          <div className="flex items-start gap-3">
            <i className="ph ph-bell text-xl text-navy mt-0.5" />
            <div className="flex-1">
              <div className="text-ink font-medium">Notifications</div>
              <p className="text-[13px] text-ink-subtle mt-0.5">
                Get a push when your invoice is ready to pay and when your scans are ready to view.
              </p>

              {!pushSupported() ? (
                <p className="text-[12px] text-ink-subtle mt-3">
                  This browser doesn't support notifications.
                </p>
              ) : iosNeedsInstall ? (
                <p className="text-[12px] text-navy mt-3">
                  On iPhone, tap the Share icon → <b>Add to Home Screen</b> first, then open the app from
                  your home screen to turn on notifications.
                </p>
              ) : pushState === "on" ? (
                <p className="text-[13px] text-sev-success mt-3 inline-flex items-center gap-1">
                  <i className="ph-fill ph-check-circle" /> Notifications are on.
                </p>
              ) : (
                <div className="mt-3">
                  <Button variant="secondary" className="h-10" disabled={pushState === "working"} onClick={enablePush}>
                    {pushState === "working" ? "Enabling…" : "Enable notifications"}
                  </Button>
                  {pushState === "denied" && (
                    <p className="text-[12px] text-ink-subtle mt-2">
                      Notifications are blocked in your browser settings.
                    </p>
                  )}
                  {pushState === "error" && (
                    <p className="text-[12px] text-sev-error mt-2">Couldn't enable notifications. Try again.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card>

        <Card className="p-1">
          <a
            href="https://atxfilmlab.com"
            className="flex items-center justify-between px-3 h-12 text-ink active:bg-line-soft rounded-xl"
          >
            <span className="flex items-center gap-3">
              <i className="ph ph-question text-lg text-ink-muted" /> Help & FAQ
            </span>
            <i className="ph ph-arrow-square-out text-ink-faint" />
          </a>
        </Card>

        <Button variant="ghost" className="text-sev-error" onClick={() => signOut.mutate()}>
          Sign out
        </Button>

        <p className="text-center text-[11px] text-ink-faint">Turbo Photo Film Lab</p>
      </div>
    </div>
  );
}
